import React, { useEffect, useState, useCallback } from 'react';
import { FiEdit3, FiTrash2, FiTrendingUp } from 'react-icons/fi';
import {
  PRIVATE_STATION_TIERS,
  PRIVATE_STATION_UPKEEP_PER_DRONE,
  buyCostT1,
  upgradeCost,
  type PrivateStationTier,
} from '@astra/shared';
import { networkManager } from '../../systems/NetworkManager';
import { onGameEvent } from '../../core/GameEvents';
import {
  PALETTE,
  stationContainerStyle,
  StationHeader,
  StationBody,
  Card,
  CardRow,
  CardLabel,
  CardValue,
  SectionTitle,
  Hint,
  ErrText,
  Loading,
  primaryBtnStyle,
  dangerBtnStyle,
  smallBtnStyle,
  iconBtnStyle,
  inputStyle,
  stationHintStyle,
} from './stationTheme';

interface Props {
  stationId: string;
  onBack: () => void;
}

interface OwnedInfo {
  id: string;
  name: string;
  tier: PrivateStationTier;
  status: 'active' | 'suspended';
  posX: number;
  posY: number;
  suspendedSince: string | null;
  lastUpkeepAt: string;
}

interface InfoPayload {
  ok: boolean;
  error?: string;
  constellationId?: string;
  slotsMax?: number;
  slotsUsed?: number;
  slotFree?: boolean;
  owned?: OwnedInfo | null;
}

export function PrivateStationPanel({ stationId, onBack }: Props) {
  const [info, setInfo] = useState<InfoPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [credits, setCredits] = useState(0);
  const [purchaseResult, setPurchaseResult] = useState<{ name: string; posX: number; posY: number } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  useEffect(() => {
    return onGameEvent('playerStateUpdate', (s) => setCredits(s.credits));
  }, []);

  // Is the docked station itself a private station owned by me?
  const dockedPrivate = (() => {
    const room: any = (networkManager as any).room;
    if (!room?.state) return null;
    return room.state.privateStations?.get?.(stationId) ?? null;
  })();

  const refresh = useCallback(() => {
    const room: any = (networkManager as any).room;
    if (!room) return;
    room.send('privateStationInfo', {});
  }, []);

  useEffect(() => {
    const room: any = (networkManager as any).room;
    if (!room) return;
    const handlers: Array<{ key: string; unsub: () => void }> = [];

    const sub = (key: string, fn: (msg: any) => void) => {
      room.onMessage(key, fn);
      handlers.push({ key, unsub: () => { /* Colyseus removes via reconnect */ } });
    };

    sub('privateStationInfo', (msg: InfoPayload) => {
      setInfo(msg);
      if (msg.owned) setRenameDraft(msg.owned.name);
    });
    sub('purchasePrivateStationResult', (msg: any) => {
      setBusy(false);
      if (msg.ok) {
        refresh();
        setError(null);
        setPurchaseResult({ name: msg.stationName || 'Your Outpost', posX: msg.posX ?? 0, posY: msg.posY ?? 0 });
      }
      else setError(reasonText(msg.reason));
    });
    sub('renamePrivateStationResult', (msg: any) => {
      setBusy(false);
      if (msg.ok) { setEditingName(false); refresh(); setError(null); }
      else setError(reasonText(msg.reason));
    });
    sub('upgradePrivateStationResult', (msg: any) => {
      setBusy(false);
      if (msg.ok) { refresh(); setError(null); }
      else setError(reasonText(msg.reason));
    });
    sub('decommissionPrivateStationResult', (msg: any) => {
      setBusy(false);
      if (msg.ok) { refresh(); setError(null); }
      else setError(reasonText(msg.reason));
    });

    refresh();
    return () => { /* no-op */ };
  }, [refresh]);

  const sendBuy = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const room: any = (networkManager as any).room;
    room?.send('purchasePrivateStation', {});
  };

  const sendRename = () => {
    if (!info?.owned || busy) return;
    const trimmed = renameDraft.trim();
    if (!trimmed) { setError('Name cannot be empty'); return; }
    setBusy(true);
    setError(null);
    const room: any = (networkManager as any).room;
    room?.send('renamePrivateStation', { stationId: info.owned.id, name: trimmed });
  };

  const sendUpgrade = () => {
    if (!dockedPrivate || busy) return;
    const nextTier = dockedPrivate.tier + 1;
    const cost = upgradeCost(dockedPrivate.tier as PrivateStationTier);
    setConfirmAction({
      title: `UPGRADE TO T${nextTier}`,
      message: `This will upgrade your station to Tier ${nextTier} and charge ${cost?.toLocaleString() ?? '?'} CR. Proceed?`,
      onConfirm: () => {
        setConfirmAction(null);
        setBusy(true);
        setError(null);
        const room: any = (networkManager as any).room;
        room?.send('upgradePrivateStation', { stationId });
      },
    });
  };

  const sendDecommission = () => {
    if (!dockedPrivate || busy) return;
    setConfirmAction({
      title: 'DECOMMISSION STATION',
      message: 'This will permanently destroy your station. Storage contents return to your global bank. Drones are lost. No credit refund. This cannot be undone.',
      onConfirm: () => {
        setConfirmAction(null);
        setBusy(true);
        setError(null);
        const room: any = (networkManager as any).room;
        room?.send('decommissionPrivateStation', { stationId });
      },
    });
  };

  // Render: management panel when docked at owned private station; otherwise lobby panel
  if (dockedPrivate) {
    return renderManagement(dockedPrivate, info, renameDraft, setRenameDraft, editingName, setEditingName, sendRename, sendUpgrade, sendDecommission, onBack, error, busy, credits, confirmAction, () => setConfirmAction(null));
  }
  return renderLobby(info, sendBuy, onBack, error, busy, credits, purchaseResult, () => setPurchaseResult(null));
}

function renderLobby(
  info: InfoPayload | null,
  sendBuy: () => void,
  onBack: () => void,
  error: string | null,
  busy: boolean,
  credits: number,
  purchaseResult: { name: string; posX: number; posY: number } | null,
  dismissPurchase: () => void,
) {
  const canAffordBuy = credits >= buyCostT1();

  // Congratulations modal after purchase
  if (purchaseResult) {
    return (
      <div style={stationContainerStyle}>
        <StationHeader onBack={onBack} title="PRIVATE STATION" hint="[ESC] Return to Station" />
        <StationBody>
          <Card>
            <SectionTitle style={{ color: PALETTE.GREEN, fontSize: 11, textAlign: 'center', marginBottom: 4 }}>Station Founded!</SectionTitle>
            <p style={{ color: PALETTE.LIGHT, fontSize: 11, lineHeight: 1.6, textAlign: 'center' }}>
              Your new outpost has been placed at coordinates{' '}
              <b style={{ color: PALETTE.TEAL }}>{Math.round(purchaseResult.posX)}, {Math.round(purchaseResult.posY)}</b>.
            </p>
            <p style={{ color: PALETTE.DIM, fontSize: 11, lineHeight: 1.5, textAlign: 'center' }}>
              It is now visible on your constellation map. Undock and travel there to manage it.
            </p>
            <button onClick={dismissPurchase} style={primaryBtnStyle(false)}>
              GOT IT
            </button>
          </Card>
        </StationBody>
      </div>
    );
  }

  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack} title="PRIVATE STATION" hint="[ESC] Return to Station" />
      <StationBody>
        {!info && <Loading />}
        {info && info.ok === false && <ErrText>{info.error ?? 'Failed to load'}</ErrText>}
        {info?.ok && (
          <>
            <Card>
              <CardRow><CardLabel>Constellation slots</CardLabel><CardValue>{info.slotsUsed} / {info.slotsMax}</CardValue></CardRow>
              <CardRow><CardLabel>Status</CardLabel><CardValue>{info.slotFree ? <span style={{color: PALETTE.GREEN}}>Slot available</span> : <span style={{color: PALETTE.RED}}>Constellation full</span>}</CardValue></CardRow>
            </Card>

            {info.owned ? (
              <Card>
                <CardRow><CardLabel>Name</CardLabel><CardValue>{info.owned.name}</CardValue></CardRow>
                <CardRow><CardLabel>Tier</CardLabel><CardValue>T{info.owned.tier}</CardValue></CardRow>
                <CardRow><CardLabel>Status</CardLabel>
                  <CardValue>{info.owned.status === 'active'
                    ? <span style={{color: PALETTE.GREEN}}>Active</span>
                    : <span style={{color: PALETTE.ORANGE}}>Suspended</span>}</CardValue>
                </CardRow>
                <CardRow><CardLabel>Coordinates</CardLabel><CardValue>{Math.round(info.owned.posX)}, {Math.round(info.owned.posY)}</CardValue></CardRow>
                <Hint>Travel to your station and dock to manage it.</Hint>
              </Card>
            ) : info.slotFree ? (
              <Card>
                <SectionTitle>Buy T1 Private Station</SectionTitle>
                <p style={{ color: PALETTE.DIM, fontSize: 11, lineHeight: 1.5 }}>
                  Founds a personal outpost in this constellation. Server picks valid coordinates;
                  the station appears immediately on your map. Costs <b style={{color: PALETTE.TEAL}}>{buyCostT1().toLocaleString()} CR</b>.
                </p>
                <button
                  disabled={busy || !canAffordBuy}
                  onClick={sendBuy}
                  style={primaryBtnStyle(busy || !canAffordBuy)}
                >
                  {busy ? 'Purchasing...' : `BUY \u2014 ${buyCostT1().toLocaleString()} CR`}
                </button>
                {!canAffordBuy && <Hint style={{ color: PALETTE.RED }}>Not enough credits.</Hint>}
              </Card>
            ) : (
              <Card>
                <SectionTitle>Constellation full</SectionTitle>
                <Hint>No private station slots are available in this constellation. Try another constellation.</Hint>
              </Card>
            )}

            {error && <ErrText>{error}</ErrText>}
          </>
        )}
      </StationBody>
    </div>
  );
}

function renderManagement(
  dockedPrivate: any,
  info: InfoPayload | null,
  renameDraft: string,
  setRenameDraft: (s: string) => void,
  editingName: boolean,
  setEditingName: (b: boolean) => void,
  sendRename: () => void,
  sendUpgrade: () => void,
  sendDecommission: () => void,
  onBack: () => void,
  error: string | null,
  busy: boolean,
  credits: number,
  confirmAction: { title: string; message: string; onConfirm: () => void } | null,
  dismissConfirm: () => void,
) {
  const tier = (dockedPrivate.tier ?? 1) as PrivateStationTier;
  const spec = PRIVATE_STATION_TIERS[tier];
  const nextCost = upgradeCost(tier);
  const canAffordUpgrade = nextCost !== null && credits >= nextCost;
  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack} title={`PRIVATE STATION \u2014 ${(dockedPrivate.name || '').toUpperCase()}`} hint="[ESC] Return to Station" />
      <StationBody>
        <Card>
          <CardRow><CardLabel>Name</CardLabel>
            <CardValue>
              {editingName ? (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <input
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    maxLength={48}
                    style={inputStyle}
                  />
                  <button disabled={busy} onClick={sendRename} style={smallBtnStyle(false)}>Save</button>
                  <button disabled={busy} onClick={() => setEditingName(false)} style={smallBtnStyle(true)}>X</button>
                </span>
              ) : (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  {dockedPrivate.name}
                  <button onClick={() => setEditingName(true)} style={iconBtnStyle}><FiEdit3 size={12} /></button>
                </span>
              )}
            </CardValue>
          </CardRow>
          <CardRow><CardLabel>Tier</CardLabel><CardValue>T{tier}</CardValue></CardRow>
          <CardRow><CardLabel>Status</CardLabel>
            <CardValue>{dockedPrivate.status === 'active'
              ? <span style={{color: PALETTE.GREEN}}>Active</span>
              : <span style={{color: PALETTE.ORANGE}}>Suspended</span>}</CardValue>
          </CardRow>
          <CardRow><CardLabel>Drone capacity</CardLabel><CardValue>{spec.droneCapacity}</CardValue></CardRow>
          <CardRow><CardLabel>Storage stacks</CardLabel><CardValue>{spec.storageStacks}</CardValue></CardRow>
          <CardRow><CardLabel>Base upkeep / day</CardLabel><CardValue>{spec.baseUpkeepPerDay.toLocaleString()} CR</CardValue></CardRow>
          <Hint>+ {PRIVATE_STATION_UPKEEP_PER_DRONE.toLocaleString()} CR per active drone, per day.</Hint>
        </Card>

        <Card>
          <SectionTitle>Upgrade</SectionTitle>
          {nextCost ? (
            <>
              <p style={{ color: PALETTE.DIM, fontSize: 11 }}>Upgrade to T{tier + 1} for <b style={{color: PALETTE.TEAL}}>{nextCost.toLocaleString()} CR</b> (instant).</p>
              <button disabled={busy || !canAffordUpgrade} onClick={sendUpgrade} style={primaryBtnStyle(busy || !canAffordUpgrade)}>
                <FiTrendingUp size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                UPGRADE TO T{tier + 1}
              </button>
              {!canAffordUpgrade && nextCost !== null && <Hint style={{ color: PALETTE.RED }}>Not enough credits.</Hint>}
            </>
          ) : (
            <Hint>Already at maximum tier.</Hint>
          )}
        </Card>

        <TransferOwnership stationId={dockedPrivate.id} />

        <Card>
          <SectionTitle>Decommission</SectionTitle>
          <p style={{ color: PALETTE.DIM, fontSize: 11 }}>
            Removes the station instantly. Storage contents return to your global bank. Drones are lost. No credit refund. This cannot be undone.
          </p>
          <button disabled={busy} onClick={sendDecommission} style={dangerBtnStyle(busy)}>
            <FiTrash2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            DECOMMISSION
          </button>
        </Card>

        {error && <ErrText>{error}</ErrText>}
      </StationBody>

      {confirmAction && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)', zIndex: 100,
        }}>
          <div style={{
            background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`,
            borderRadius: 6, padding: 24, maxWidth: 400, width: '90%',
          }}>
            <div style={{ color: PALETTE.LIGHT, fontWeight: 700, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em', marginBottom: 12 }}>
              {confirmAction.title}
            </div>
            <p style={{ color: PALETTE.DIM, fontSize: 11, lineHeight: 1.6, marginBottom: 20 }}>
              {confirmAction.message}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={dismissConfirm} style={{
                padding: '8px 16px', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                background: 'transparent', color: PALETTE.DIM, border: `1px solid ${PALETTE.DIM}`,
                borderRadius: 4, cursor: 'pointer', letterSpacing: '0.05em',
              }}>CANCEL</button>
              <button onClick={confirmAction.onConfirm} style={{
                padding: '8px 16px', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                background: 'transparent',
                color: confirmAction.title.includes('DECOMMISSION') ? PALETTE.RED : PALETTE.TEAL,
                border: `1px solid ${confirmAction.title.includes('DECOMMISSION') ? PALETTE.RED : PALETTE.TEAL}`,
                borderRadius: 4, cursor: 'pointer', letterSpacing: '0.05em',
              }}>CONFIRM</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Transfer this station's ownership to another pilot or to a corporation (a pure
 * gift; the recipient must accept). Transferring loses the station and its drones,
 * so it carries a strong confirmation. See corp.md §1.7.
 */
function TransferOwnership({ stationId }: { stationId: string }) {
  const [recipientType, setRecipientType] = useState<'player' | 'corp'>('corp');
  const [playerName, setPlayerName] = useState('');
  const [myCorps, setMyCorps] = useState<{ id: string; name: string; tag: string }[]>([]);
  const [corpId, setCorpId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const room: any = (networkManager as any).room;
    if (!room) return;
    room.onMessage('myCorporations', (m: { corps: any[] }) => {
      const list = (m.corps ?? []).map((c) => ({ id: c.id, name: c.name, tag: c.tag }));
      setMyCorps(list);
      if (list.length && !corpId) setCorpId(list[0].id);
    });
    room.onMessage('createTransferOfferResult', (r: any) => {
      setBusy(false);
      setConfirming(false);
      setMsg(r?.success ? 'Offer sent — the recipient must accept it.' : `Failed: ${r?.error ?? 'unknown'}`);
    });
    room.send('getMyCorporations', {});
    return () => { /* handlers persist for room lifetime */ };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = () => {
    const room: any = (networkManager as any).room;
    if (!room) return;
    const toRecipient = recipientType === 'player' ? playerName.trim() : corpId;
    if (!toRecipient) { setMsg('Choose a recipient.'); return; }
    setBusy(true);
    setMsg(null);
    room.send('createTransferOffer', { assetType: 'private_station', assetId: stationId, toOwnerType: recipientType, toRecipient });
  };

  return (
    <Card>
      <SectionTitle>Transfer ownership</SectionTitle>
      <p style={{ color: PALETTE.DIM, fontSize: 11 }}>
        Gift this station to another pilot or a corporation. The recipient must accept. You lose the station and all its drones.
      </p>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button style={smallBtnStyle(recipientType !== 'corp')} onClick={() => setRecipientType('corp')}>Corporation</button>
        <button style={smallBtnStyle(recipientType !== 'player')} onClick={() => setRecipientType('player')}>Pilot</button>
      </div>
      {recipientType === 'corp' ? (
        myCorps.length ? (
          <select style={inputStyle} value={corpId} onChange={(e) => setCorpId(e.target.value)}>
            {myCorps.map((c) => <option key={c.id} value={c.id}>{c.name} [{c.tag}]</option>)}
          </select>
        ) : <Hint>You are not part of any corporation.</Hint>
      ) : (
        <input style={inputStyle} value={playerName} placeholder="pilot name" onChange={(e) => setPlayerName(e.target.value)} />
      )}
      {!confirming ? (
        <button style={dangerBtnStyle(busy)} disabled={busy} onClick={() => setConfirming(true)}>TRANSFER OWNERSHIP</button>
      ) : (
        <div style={{ marginTop: 8 }}>
          <p style={{ color: PALETTE.RED, fontSize: 11 }}>This is irreversible — you give up the station and its drones. Confirm?</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={dangerBtnStyle(busy)} disabled={busy} onClick={submit}>CONFIRM TRANSFER</button>
            <button style={smallBtnStyle(false)} onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <Hint style={{ color: msg.startsWith('Offer') ? PALETTE.GREEN : PALETTE.RED }}>{msg}</Hint>}
    </Card>
  );
}

function reasonText(reason: string | undefined): string {
  switch (reason) {
    case 'no_credits': return 'Not enough credits.';
    case 'already_owned': return 'You already own a private station in this constellation.';
    case 'constellation_full': return 'No slots available in this constellation.';
    case 'no_placement': return 'Could not find a valid placement spot.';
    case 'constellation_not_found': return 'Constellation not found.';
    case 'not_docked': return 'You must be docked at the station.';
    case 'not_owner': return 'Only the owner can do that.';
    case 'max_tier': return 'Already at maximum tier.';
    case 'invalid_name': return 'Invalid name (1–48 characters).';
    case 'not_found': return 'Station not found.';
    default: return reason ?? 'Unknown error';
  }
}


export default PrivateStationPanel;
